import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpParams
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SecurityService } from '../services/security.service';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {

  private readonly encryptUrlPatterns: RegExp[] = [
    /api\/user\/login$/i,
    /token$/i,
    /api\/user\/register$/i,
    /api\/user\/resetpassword$/i,
    /api\/user\/recoverpassword$/i,
    /api\/user\/create$/i,
    /api\/user\/updateprofile$/i,
  ];

  private readonly encodePatterns: RegExp[] = [
    /api\/user\/login$/i,
    /token$/i
  ];

  constructor(private security: SecurityService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const shouldEncrypt = this.encryptUrlPatterns.some(pattern => pattern.test(req.url));
    const shouldEncode = this.encodePatterns.some(pattern => pattern.test(req.url));

    if (!shouldEncrypt && !shouldEncode) {
      return next.handle(req);
    }

    // Wrap async work in a Promise
    return from(this.handleEncryption(req, shouldEncode)).pipe(
      switchMap(encryptedReq => next.handle(encryptedReq))
    );
  }

  private async handleEncryption(req: HttpRequest<any>, encode: boolean): Promise<HttpRequest<any>> {
    let body = req.body;
    let params = req.params;

    if (body && typeof body === 'object' && !(body instanceof HttpParams) && !(body instanceof FormData)) {
      body = await this.processPasswordFields(body, encode);
    }

    if (body instanceof HttpParams) {
      let newBody = new HttpParams({ encoder: body['encoder'] });
      for (const key of body.keys()) {
        const value = body.get(key);
        if (key.toLowerCase().includes('password') && value) {
          const encrypted = await this.security.encrypt(value);
          newBody = newBody.set(key, encode ? encodeURIComponent(encrypted) : encrypted);
        } else if (value) {
          newBody = newBody.set(key, value);
        }
      }
      body = newBody;
    }

    if (params && params.keys().length > 0) {
      let newParams = params;
      for (const key of params.keys()) {
        const value = params.get(key);
        if (key.toLowerCase().includes('password') && value) {
          const encrypted = await this.security.encrypt(value);
          newParams = newParams.set(key, encode ? encodeURIComponent(encrypted) : encrypted);
        }
      }
      params = newParams;
    }

    return req.clone({ body, params });
  }

  private async processPasswordFields(data: any, encode: boolean): Promise<any> {
    if (Array.isArray(data)) {
      return Promise.all(data.map(item => this.processPasswordFields(item, encode)));
    }

    if (data !== null && typeof data === 'object') {
      const cloned: any = {};
      for (const key of Object.keys(data)) {
        const value = data[key];

        if (key.toLowerCase().includes('password') && value) {
          const encrypted = await this.security.encrypt(value);
          cloned[key] = encode ? encodeURIComponent(encrypted) : encrypted;
        } else if (typeof value === 'object' && value !== null) {
          cloned[key] = await this.processPasswordFields(value, encode);
        } else {
          cloned[key] = value;
        }
      }
      return cloned;
    }

    return data;
  }
}